import React, { useEffect, useReducer } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
    Button, Grid, IconButton, makeStyles,
} from '@material-ui/core';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import RefreshOutlinedIcon from '@material-ui/icons/RefreshOutlined';
import clsx from 'clsx';
import { concat } from 'lodash';
import update from 'immutability-helper';

import InputSearch from 'components/widgets/InputSearch';
import Select from 'components/widgets/Select';
import VirtualTable from 'components/widgets/VirtualTable';
import ModalUtils from 'utils/ModalUtils';
import { DataSort, FetchPolicy } from 'utils/enum/Core';
import {
    EmployeeActiveFilter,
    EmployeeGroupFilter,
} from 'utils/enum/EmployeeEnum';
import { modules, HR_SUBMODULES } from 'utils/enum/modules';
import PhoneUtils from 'lib/PhoneUtils';
import StringUtils from 'lib/StringUtils';
import EmployeeQuery from 'services/graphQL/query/hr/EmployeeQuery';
import LoginAndPermissionStyles from 'styles/modules/hr/LoginAndPermissionStyles';

const useStyles = makeStyles((theme) => LoginAndPermissionStyles.list(theme));

const PAGE_SIZE = 25;

const ACTIVE_STATUS_OPTIONS = [
    { value: EmployeeActiveFilter.ACTIVE, label: 'Active Status: Active' },
    { value: EmployeeActiveFilter.INACTIVE, label: 'Active Status: Inactive' },
    { value: EmployeeActiveFilter.ALL, label: 'Active Status: All' },
];

const ROLE_OPTIONS = [
    { value: EmployeeGroupFilter.ALL, label: 'Role: All Types' },
    { value: EmployeeGroupFilter.EMPLOYEE, label: 'Role: Employee' },
    { value: EmployeeGroupFilter.NONEMPLOYEE, label: 'Role: Non-Employee' },
];

const ACTION_TYPE = {
    LOAD_MORE: 'loadMore',
    SET_OFFSET: 'setOffset',
    ON_SEARCH: 'onSearch',
    SET_FILTER: 'setFilter',
    ON_CHANGE_SORT: 'onChangeSort',
    REFRESH: 'refresh',
};

const initState = {
    records: [],
    totalCount: 0,
    offset: 0,
    search: '',
    active: EmployeeActiveFilter.ACTIVE,
    employeeGroup: EmployeeGroupFilter.ALL,
    sortBy: 'fullName',
    sortDir: DataSort.ASC,
};

const listReducer = (state, action) => {
    switch (action.type) {
    case ACTION_TYPE.LOAD_MORE: {
        const incoming = action.records?.data || [];
        const records = state.offset === 0
            ? incoming
            : concat(state.records, incoming.filter(
                (item) => !state.records.some((existing) => existing.userId === item.userId),
            ));

        return update(state, {
            records: { $set: records },
            totalCount: { $set: action.records?.totalCount || 0 },
        });
    }
    case ACTION_TYPE.SET_OFFSET:
        return update(state, {
            offset: { $set: action.value },
        });
    case ACTION_TYPE.ON_SEARCH:
        return update(state, {
            search: { $set: action.value },
            offset: { $set: 0 },
            records: { $set: [] },
            totalCount: { $set: 0 },
        });
    case ACTION_TYPE.SET_FILTER:
        return update(state, {
            [action.field]: { $set: action.value },
            offset: { $set: 0 },
            records: { $set: [] },
            totalCount: { $set: 0 },
        });
    case ACTION_TYPE.ON_CHANGE_SORT:
        return update(state, {
            sortBy: { $set: action.sortBy },
            sortDir: { $set: action.sortDir },
            offset: { $set: 0 },
            records: { $set: [] },
            totalCount: { $set: 0 },
        });
    case ACTION_TYPE.REFRESH:
        return update(state, {
            offset: { $set: 0 },
            records: { $set: [] },
            totalCount: { $set: 0 },
        });
    default:
        return state;
    }
};

const LoginAndPermissionList = () => {
    const classes = useStyles();
    const history = useHistory();
    const [state, dispatch] = useReducer(listReducer, initState);

    const filter = {
        search: StringUtils.isEmpty(state.search) ? null : state.search,
        active: state.active,
        employeeGroup: state.employeeGroup,
    };

    const {
        data, loading, error, refetch,
    } = useQuery(EmployeeQuery.GET_EMPLOYEE_LIST, {
        variables: {
            filter,
            paginate: {
                init: state.offset,
                limit: PAGE_SIZE,
            },
            sort: {
                field: state.sortBy,
                dir: state.sortDir,
            },
        },
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
        notifyOnNetworkStatusChange: true,
    });

    useEffect(() => {
        if (error) {
            ModalUtils.errorMessage(error?.graphQLErrors);
            return;
        }

        if (!loading && data?.getEmployeeList) {
            dispatch({
                type: ACTION_TYPE.LOAD_MORE,
                records: data.getEmployeeList,
            });
        }
    }, [loading, data, error]);

    const loadMore = () => {
        if (loading || state.records.length >= state.totalCount) return;
        dispatch({ type: ACTION_TYPE.SET_OFFSET, value: state.records.length });
    };

    const onSearch = (value) => {
        if (state.search !== value) {
            dispatch({ type: ACTION_TYPE.ON_SEARCH, value });
        }
    };

    const onFilterChange = (name, value) => {
        if (state[name] === value) return;
        dispatch({ type: ACTION_TYPE.SET_FILTER, field: name, value });
    };

    const onSort = ({ sortBy, sortDirection }) => {
        dispatch({
            type: ACTION_TYPE.ON_CHANGE_SORT,
            sortBy,
            sortDir: sortDirection,
        });
    };

    const onRefresh = () => {
        dispatch({ type: ACTION_TYPE.REFRESH });
        refetch({
            filter,
            paginate: {
                init: 0,
                limit: PAGE_SIZE,
            },
            sort: {
                field: state.sortBy,
                dir: state.sortDir,
            },
        });
    };

    const openEmployeeDetail = (employee) => {
        history.push(
            `/${modules.HR}/${HR_SUBMODULES.LOGIN_AND_PERMISSION}/${employee.userId}`,
            { employee },
        );
    };

    const columns = [
        {
            headerClassName: classes.tableHeader,
            label: '',
            dataKey: 'status',
            width: 40,
            disableSort: true,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;
                const isOnline = String(record.status || '').toLowerCase() === 'online';
                return (
                    <span
                        className={clsx(
                            classes.statusDot,
                            isOnline ? classes.statusActive : classes.statusInactive,
                        )}
                        title={isOnline ? 'Online' : 'Offline'}
                    />
                );
            },
        },
        {
            headerClassName: classes.tableHeader,
            label: 'ID',
            dataKey: 'userId',
            width: 90,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;
                return (
                    <span className={classes.text}>{record.userId}</span>
                );
            },
        },
        {
            headerClassName: classes.tableHeader,
            label: 'Name',
            dataKey: 'fullName',
            width: 180,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;
                return (
                    <span className={classes.text}>{record.fullName}</span>
                );
            },
        },
        {
            headerClassName: classes.tableHeader,
            label: 'Username',
            dataKey: 'userName',
            width: 150,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;
                return (
                    <span className={classes.text}>{record.userName}</span>
                );
            },
        },
        {
            headerClassName: classes.tableHeader,
            label: 'Department',
            dataKey: 'department',
            width: 140,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;
                return (
                    <span className={classes.text}>{record.department || ''}</span>
                );
            },
        },
        {
            headerClassName: classes.tableHeader,
            label: 'Users Lot',
            dataKey: 'usersLot',
            width: 140,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;
                return (
                    <span className={classes.text}>{record.usersLot || ''}</span>
                );
            },
        },
        {
            headerClassName: classes.tableHeader,
            label: 'Employee Type',
            dataKey: 'employeeType',
            width: 160,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;
                return (
                    <span className={classes.text}>{record.employeeType || ''}</span>
                );
            },
        },
        {
            headerClassName: classes.tableHeader,
            label: 'Work Email',
            dataKey: 'workEmail',
            width: 220,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;
                return (
                    <span className={classes.text}>{record.workEmail || ''}</span>
                );
            },
        },
        {
            headerClassName: classes.tableHeader,
            label: 'Cell Phone',
            dataKey: 'cellPhone',
            width: 130,
            disableSort: true,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;
                return (
                    <span className={classes.text}>
                        {PhoneUtils.format(record.cellPhone) || record.cellPhone || ''}
                    </span>
                );
            },
        },
        {
            headerClassName: classes.tableHeader,
            label: '',
            dataKey: 'actions',
            width: 56,
            disableSort: true,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;
                const employeeName = record.fullName || record.userName || record.userId;
                return (
                    <IconButton
                        size="small"
                        aria-label={`Edit login and permissions for ${employeeName}`}
                        onClick={(event) => {
                            event.stopPropagation();
                            openEmployeeDetail(record);
                        }}
                    >
                        <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                );
            },
        },
    ];

    const rowStyleFormat = () => ({
        cursor: 'pointer',
    });

    const parentWidth = document.getElementById('table-container')?.clientWidth;
    let tableWidth = columns.reduce((sum, col) => sum + col.width, 0);
    if (parentWidth > tableWidth) tableWidth = parentWidth - 30;

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <div className={classes.headerLeft}>
                    <InputSearch
                        size="sm"
                        initialSearch={state.search}
                        customClasses={classes.search}
                        placeholder="Search by name, ID, lot, phone or email..."
                        onSearch={onSearch}
                        executeWhenClearButton={() => onSearch('')}
                    />
                </div>
                <div className={classes.headerRight}>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<RefreshOutlinedIcon />}
                        onClick={onRefresh}
                        disabled={loading}
                    >
                        Refresh
                    </Button>
                    <Select
                        name="active"
                        className={classes.filterSelect}
                        options={ACTIVE_STATUS_OPTIONS}
                        value={state.active}
                        onChange={onFilterChange}
                        size="sm"
                    />
                    <Select
                        name="employeeGroup"
                        className={classes.filterSelect}
                        options={ROLE_OPTIONS}
                        value={state.employeeGroup}
                        onChange={onFilterChange}
                        size="sm"
                    />
                </div>
            </div>
            <div className={classes.box}>
                <Grid className={classes.tableGrid} item xs={12}>
                    <div id="table-container" className={clsx(classes.tableContainer, 'virtual-table-base')}>
                        {parentWidth > 0 && (
                            <VirtualTable
                                loading={loading}
                                rowHeight={45}
                                totalRecords={state.totalCount}
                                data={state.records}
                                columns={columns}
                                width={tableWidth}
                                loadMore={loadMore}
                                sort={onSort}
                                sortBy={state.sortBy}
                                sortDirection={state.sortDir}
                                rowStyleFormat={rowStyleFormat}
                                onRowDoubleClick={({ rowData }) => {
                                    if (rowData) openEmployeeDetail(rowData);
                                }}
                            />
                        )}
                    </div>
                </Grid>
            </div>
        </div>
    );
};

export default LoginAndPermissionList;
